import { JSClassParameter } from "../../../../../components/LiveInterfaceCustomComponents/LiveInterface";

export type JSClassObject = {
  group: string;
  ownName: string;
  parameter?: string | number;
  parameterName?: "Id" | "ApiId";
  parentClassName?: string;
  undotted?: string;
};

/**
 * @example JS-MyComponent-Root             The root of the "MyComponent" group
 * @example JS-MyComponent-Title            The "Title" component of the "MyComponent" group
 * @example JS-Product-Root                 The root of the "Product" group
 * @example JS-Product-Root--1234           This one is annotated with product.id
 * @example JS-Product-Root--ApiId5001      This one is annotated with a parameter, "ApiId" = "5001"
 * @example JS-Product-Card-Header          You are allowed to nest names
 * @example bad: JS-Product-Card--Selected  Alternate states need a specific syntax, this is a TODO
 * @example bad: JS-product-Root            No uppercase group name
 * @example bad: Product-Root               Doesn't start with "JS"
 * @example bad: JS-Product--1234api        Parameters must follow precise formats. Either --{NUMBER} for id OR --{NAME}{VALUE} for other parameters
 * @param JSClass
 */
export function parseJSClassName(JSClass: string): JSClassObject {
  // fallback values if formal parsing doesn't work
  const groupNameFromClass = JSClass.split("-");
  const obj: JSClassObject = {
    group: groupNameFromClass && groupNameFromClass[2] ? groupNameFromClass[1] : "General",
    ownName: JSClass.replace(/\.?JS-[a-zA-Z0-9]+-/, "").replace(/(--\d+)?/g, ""),
  };

  const matches = JSClass.match(
    /\.?JS-(?<group>[A-Z][a-zA-Z0-9]+)(-(?<ownName>[A-Z][a-zA-Z0-9]+))+(--(?<parameterName>[A-Z][a-zA-Z]+)?(?<parameter>\d+))?/
  );
  if (matches && matches.groups) {
    obj.group = matches.groups.group;
    obj.ownName = matches.groups.ownName;
    const parameter = matches.groups.parameter;
    if (parameter && !matches.groups.parameterName) {
      obj.parameter = parseInt(parameter, 10);
      obj.parameterName = "Id";
    } else if (parameter && matches.groups.parameterName) {
      obj.parameter = parameter;
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      obj.parameterName = matches.groups.parameterName;
    }
  } else {
    // console.debug("Failed to parse JS class: " + JSClass);
  }

  obj.undotted = JSClass.startsWith(".") ? JSClass.replace(/^\./, "") : JSClass;
  obj.parentClassName = `.${obj.undotted}`;
  return obj;
}

export function addParameterToJSClass(
  JSClass: string,
  parameterName: JSClassParameter["parameterName"],
  value: JSClassParameter["value"]
) {
  if (parameterName === "id") {
    return `${JSClass}--${value}`;
  }
  return `${JSClass}--${parameterName}${value}`;
}
